import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button, ErrorMessage, Icon, Loading, Modal, TextArea, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { driverProfileApi } from "@/redux/apis";
import { useUpdateFleetStatusMutation } from "@/redux/apis/driver/driverApi";
import { useAppDispatch } from "@/redux/hooks";
import { addToast } from "@/utils";
import { useProfileContext } from "../context";
import { RemoveDriverFormData, removeDriverFormSchema } from "../form";

type RemoveStep = "choice" | "terminate" | "driver-left";

interface RemoveDriverModalProps {
  open: boolean;
  onClose: () => void;
}

export const RemoveDriverModal = ({ open, onClose }: RemoveDriverModalProps) => {
  const [step, setStep] = useState<RemoveStep>("choice");

  const handleClose = () => {
    setStep("choice");
    onClose();
  };

  if (step === "choice") {
    return <RemoveChoiceModal open={open} onClose={handleClose} onSelect={setStep} />;
  }

  return <RemoveReasonModal open={open} onClose={handleClose} onBack={() => setStep("choice")} type={step} />;
};

const RemoveChoiceModal = ({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (step: RemoveStep) => void;
}) => {
  return (
    <Modal open={open} onClose={onClose} className="w-[80vw] md:w-[514px] lg:w-[514px]">
      <div className="flex flex-col items-start">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
          <Icon name="Danger" variant="Bold" size="lg" className="text-danger" />
        </div>
      </div>
      <div className="mt-4">
        <Typography variant="title">How would you like to remove this driver?</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          Choose how you want to remove this driver from your operations
        </Typography>
      </div>
      <div className="mt-6 flex flex-col gap-3">
        <Button size="lg" variant="secondary" className="w-full justify-start bg-neutral-gray hover:bg-white" onClick={() => onSelect("terminate")}>
          Terminate Driver
        </Button>
        <Button size="lg" variant="secondary" className="w-full justify-start bg-neutral-gray hover:bg-white" onClick={() => onSelect("driver-left")}>
          Driver Has Left
        </Button>
      </div>
      <div className="mt-4">
        <Button size="lg" variant="secondary" className="w-full" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const RemoveReasonModal = ({
  open,
  onClose,
  onBack,
  type,
}: {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  type: "terminate" | "driver-left";
}) => {
  const dispatch = useAppDispatch();
  const { driver } = useProfileContext();
  const [updateFleetStatus, { isLoading }] = useUpdateFleetStatusMutation();

  const isTerminate = type === "terminate";
  const title = isTerminate ? "Reason for Terminate Driver" : "Reason for Driver Has Left";
  const description = isTerminate
    ? "Terminating a driver will remove them from your operations and prevent them from doing any future work with you."
    : "Removing a driver as 'Driver Has Left' will take them out of your team and free up their account to join another operator.";
  const submitLabel = isTerminate ? "Terminate Driver" : "Confirm Driver Has Left";
  const accountStatus = isTerminate ? "removed" : "left";

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<RemoveDriverFormData>({
    resolver: zodResolver(removeDriverFormSchema),
  });

  const onSubmit = handleSubmit((data) => {
    updateFleetStatus({
      id: driver.uuid,
      account_status: accountStatus,
      account_status_reason: data.reason,
    })
      .unwrap()
      .then(() => {
        dispatch(driverProfileApi.util.invalidateTags(["DriverProfile"]));
        addToast("success", isTerminate ? "Successfully terminated driver." : "Successfully marked driver as left.");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  return (
    <Modal open={open} onClose={() => (isLoading ? null : onClose())} className="relative w-[80vw] md:w-[400px] lg:w-[400px]">
      {isLoading && <Loading />}
      <div className="flex flex-col gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
          <Icon name="Danger" variant="Bold" size="lg" className="text-danger" />
        </div>
      </div>
      <form onSubmit={onSubmit} className="mt-4">
        <Typography variant="title">{title}</Typography>
        <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
          {description}
        </Typography>
        <div className="mt-5">
          <TextArea hideCharCount placeholder="Enter your reason" hasError={!!formErrors.reason} maxLength={2048} {...register("reason")} />
          <ErrorMessage errors={formErrors} name="reason" />
          <Typography variant="paragraph" className="mt-1 text-neutral-silver-gray">
            The reason you provide will remain confidential
          </Typography>
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <Button type="submit" size="lg" variant="primary" className="w-full border-danger bg-danger hover:bg-danger-dark" disabled={isLoading}>
            {submitLabel}
          </Button>
          <Button size="lg" variant="secondary" className="w-full" onClick={onBack} disabled={isLoading}>
            Back
          </Button>
        </div>
      </form>
    </Modal>
  );
};
